<template>
    <div 
        class="page-details"
        @focusin="onFocusDetailMovie"
        @focusout="onFocusoutDetailMovie"
    >
        <details-header
            ref="showInfo"
            :item="currentItem"
            :is-playable="true"
            :legacy-play="true"
            @unset-focused="focusFirstRow"
            @play="play"
            @show-trailer="showTrailer"
        />
        <div>
            <Lines-detail-movie 
                ref="linesDetailMovie" 
                @ready="readyLines"
                @list-item-selected="itemsSelected"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import LinesDetailMovie from "@/components/widgets/LinesDetailMovie/LinesDetailMovie.vue";
    import {PlayItem} from "@ca.vmedia.tv/domains";
    import {AppConstants} from "@/common";
    import type {DetailsHeaderRef} from "@/components/widgets/DetailsHeader/types";
    import type {RelatedOnDemandCardData, CreditCardData} from "@/components/Cards/types";
    import {linkItems} from "@/components/Header/types";
    import { useApplicationStore } from "@/stores/Application";
    import {Logger} from '@/utils';
    import type { LinesDetailMovieRef } from "@/components/widgets/LinesDetailMovie/types";
    import { computed, ref, type Ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
    import { useRouter } from 'vue-router';
    import { useAccountStore } from "@/stores/Account";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { storeToRefs } from 'pinia';
    import { useFocus } from "@/composables/useFocus";
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { Strings } from "@/locales/types/enums/strings";
    import { ScreenName } from "@/common/types/screen-names";
    import { Param as AnalyticsParam } from "@/plugins/analytics/interfaces/analytics.service.interface";
    import { useGenresStore } from "@/stores/Genres";
    import { useCastPageStore } from "@/stores/CastPage";
   
    const {
        setViewedCastDetailItems
    } = useCastPageStore();

    const DetailPageStoreModule = useDetailPageStore();
    const {
        getCurrentItemDetail: currentItem,
    } = storeToRefs(DetailPageStoreModule);
    const {
        setViewedDetailItems
    } = DetailPageStoreModule;

    const ApplicationStore = useApplicationStore();
    const {
        getIsPreviewSingInModal: isPreviewSignInModal,
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const GenresStoreModule = useGenresStore();
    const {
        getTopGenres,
    } = storeToRefs(GenresStoreModule);

    const { setIsHaveFocusedElement, focusElementLine } = useFocus();

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-detail-movie'],
    });

    const AccountStoreModule = useAccountStore()
    const {
        getForgotModalVisible: isForgotModalVisible
    } = storeToRefs(AccountStoreModule);
    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin
    } = storeToRefs(ParentControlStoreModule);


    const router = useRouter();
    const { $fire, $eventBus, $navigator, $analytics } = useNuxtApp();
    const { t } = useI18n();

    const traceDetailsScreen = $fire.trace($fire.performance, 'DETAILS_SCREEN MOVIE');
    try {
        traceDetailsScreen.start();
        Logger.captureLog('DETAILS_SCREEN MOVIE start');
    } catch (e){
        Logger.captureLog(e);
    }

    const showInfo: Ref<DetailsHeaderRef | null> = ref(null);
    
    const linesDetailMovie: Ref<LinesDetailMovieRef | null>= ref(null);
    const isRegKeyListenersDetailMovie: Ref<boolean> = ref(false);

    const isReadyLines: Ref<boolean>= ref(false);
    const isLoadedComponents = computed((): boolean => linesDetailMovie.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && isLoadedComponents.value);

    const allowRegistrationKeyListeners = computed((): boolean => !isTypeModeEnabledPin.value && !isForgotModalVisible.value);
    
    const focusToDetailHeader = () => {
        if (showInfo.value) {
            showInfo.value.focus();
        }
    };
    const readyLines = () => isReadyLines.value = true;
    const showTrailer = (trailerVideoId: string): void => {
        if (trailerVideoId) {
            void router.push(`${linkItems.trailer.routerPath}?youtubeVideoId=${trailerVideoId}`);
        }
    };
    const itemsSelected = (val: IEventSelected) => {
        switch (val.lineId) {
            case AppConstants.shelfKeys.relatedCards:
                onRelatedSelectedDetailMovie(val.item as RelatedOnDemandCardData);
                break; 
            case AppConstants.shelfKeys.castCards:
                onCastSelectedDetailMoviesPage(val.item as CreditCardData);
                break; 
        }
    };

    const focusFirstRow = () => {
        if (linesDetailMovie.value) {
            linesDetailMovie.value.focusFirstRow();
        }
    };
    const focusElement = () => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusToDetailHeader();
        }
    };
    const onRelatedSelectedDetailMovie = (onDemandItem: RelatedOnDemandCardData) => {
        try {
            const playItem = onDemandItem.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(onDemandItem));
            }
            setViewedDetailItems(playItem);

            const [ id ] = onDemandItem.getId().split('_');

            const path = PlayItem.isSeries(playItem) ?
                `${linkItems.detailsVodSeries.routerPath}/${id}` :
                `${linkItems.detailsVodMovies.routerPath}/${id}`;

            void router.push(path);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onCastSelectedDetailMoviesPage = (credit: CreditCardData) => {
        try {
            const creditItem = credit.getCreditItem();
            if (!creditItem) {
                throw new Error("СreditItem is null:" + JSON.stringify(credit));
            }

            const personId = credit.getPersonId();
            if (!personId) {
                throw new Error("The person ID is null:" + JSON.stringify(credit));
            }

            setViewedCastDetailItems(creditItem);

            void router.push(`${linkItems.detailCast.routerPath}/${personId}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const play = () => {
        void router.push(linkItems.player.routerPath);
    };
    const onFocusDetailMovie = () => {
        registerKeyListenersDetailMovie();
    };
    const onFocusoutDetailMovie = () => {
        unregisterKeyListenersDetailMovie();
    };
    const registerKeyListenersDetailMovie = () => {
        if (!isRegKeyListenersDetailMovie.value) {
            $navigator.on("keypress:back", keyBackListenerDetailMovie);
            isRegKeyListenersDetailMovie.value = true;
        }
    };
    const unregisterKeyListenersDetailMovie = () => {
        if (isRegKeyListenersDetailMovie.value) {
            $navigator.off("keypress:back", keyBackListenerDetailMovie);
            isRegKeyListenersDetailMovie.value = false;
        }
    };
    const keyBackListenerDetailMovie = () => {
        router.go(-1);
    };
    const initFocus = (): void => {
        setIsHaveFocusedElement();
        focusElement();
    };

    const handlerReady = (): void => {
       void nextTick(() => {
            initFocus();
            if (traceDetailsScreen) {
                try {
                    traceDetailsScreen.stop();
                    Logger.captureLog(`DETAILS_SCREEN MOVIE stop ${traceDetailsScreen.durationUs / 1000000} sec`);
                } catch (e){
                    Logger.captureLog(e);
                }
            }
        });
    };

    watch(
        isReadyAll,
        (value: boolean) => {
            if (value) {
                handlerReady();
            }
        }
    );

    watch(
        allowRegistrationKeyListeners,
        (value: boolean) => {
            if (value) {
                initFocus();
            } else {
                unregisterKeyListenersDetailMovie();
            }
        }
    );

    watch(
        isPreviewSignInModal,
        (value: boolean) => {
            if (value) {
                unregisterKeyListenersDetailMovie();
            } else {
                initFocus();
            }
        }
    )

    onMounted(() => {
       void nextTick(() => {
            $eventBus.on("key-up-rows", focusToDetailHeader);
           void nextTick((): void => {
                if (currentItem.value) {
                    const currentGenre = getTopGenres.value?.find((item) => item.id === currentItem.value?.topGenreId);

                    $analytics.createCustomScreenEvent({
                        [AnalyticsParam.FA_SCREEN_NAME]: ScreenName.VOD_MOVIE_DETAILS,
                        [AnalyticsParam.FA_SCREEN_CLASS]: ScreenName.VOD_MOVIE_DETAILS,
                        [AnalyticsParam.ITEM_ID]: currentItem.value.id ? String(currentItem.value.id) : null,
                        [AnalyticsParam.CONTENT_TYPE]: currentItem.value.contentType,
                        [AnalyticsParam.CHANNEL_ID]: currentItem.value.topGenreId,
                        [AnalyticsParam.CHANNEL_NAME]: currentGenre?.name,
                        [AnalyticsParam.MOVIE_NAME]: currentItem.value?.name,
                    })
                }
            });
        });
    });

    onBeforeUnmount(() => {
        $eventBus.off("key-up-rows", focusToDetailHeader);
        unregisterKeyListenersDetailMovie();
    });
</script>

<style lang="scss">
    @import url("@/assets/styles/pages/_details.scss");
</style>